import { Dispatch, SetStateAction, useEffect } from 'react';
import { Slider } from '@care/react-component-lib';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  Typography,
  type Theme,
} from '@mui/material';
import { Icon24UtilityChevron } from '@care/react-icons';
import { loadFilters } from '@/utils/filtersStorage';

interface PayRatesProps {
  hourlyRate: [number, number];
  setHourlyRate: Dispatch<SetStateAction<[number, number]>>;
}

const PayRate = ({ hourlyRate, setHourlyRate }: PayRatesProps) => {
  const localStorageFilters = loadFilters();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // check if hourlyRate filter is currently applied
  useEffect(() => {
    const currentHourlyRate = (): [number, number] => {
      const previousHourlyRate: string[] | undefined = localStorageFilters?.hourlyRate?.split('-');

      if (previousHourlyRate && previousHourlyRate?.length === 2) {
        const [previousMin, previousMax] = previousHourlyRate;
        return [Number(previousMin), Number(previousMax)];
      }
      return hourlyRate;
    };
    setHourlyRate(currentHourlyRate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePayRateChanges = (val: number | number[]) => {
    if (Array.isArray(val) && val.length === 2) {
      const [minHourlyRate, maxHourlyRate] = val;
      setHourlyRate([minHourlyRate, maxHourlyRate]);
    }
  };

  const slider = (
    <Box
      mt={1}
      pl={1}
      pr={2.5}
      sx={{
        maxWidth: '327px',
      }}>
      <Slider
        appendRange
        max={50}
        min={10}
        step={1}
        prefix="$"
        unit="/ hour"
        value={hourlyRate}
        onChange={handlePayRateChanges}
        aria-label="range-slider"
        sx={(theme: Theme) => ({
          '& .MuiSlider-root': {
            padding: '13px 0',
          },
          '& span.bigLabel': {
            marginLeft: theme.spacing(-1),
          },
        })}
      />
    </Box>
  );

  const mobileView = (
    <>
      <Typography variant="h3">Pay rate</Typography>
      {slider}
    </>
  );

  const desktopView = (
    <Accordion elevation={0} defaultExpanded>
      <AccordionSummary
        expandIcon={<Icon24UtilityChevron />}
        sx={{ padding: 0, width: 'fit-content' }}>
        <Typography variant="h3">Pay rate</Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={(theme) => ({
          padding: theme.spacing(0, 0, 0, 0.5),
        })}>
        {slider}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box component="section" mb={2}>
      {isMobile ? mobileView : desktopView}
    </Box>
  );
};

export { PayRate };
